import type { SelectProps } from 'antd';
import { Form, Select } from 'antd';
import styled from 'styled-components';

import { LoadingOutlined } from '@ant-design/icons';
import { Button } from '@components/Button';
import { Checkbox } from '@components/Checkbox';
import { Input } from '@components/Input';
import { PhoneInput } from '@components/Input/PhoneInput';
import { Label } from '@components/Label';

export const StyledSubmitButton = styled(Button)`
  width: 100%;
  display: flex;
  justify-content: center;
  margin-top: 24px;
  margin-bottom: 16px;
`;

export const CustomLogout = styled.span`
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
`;

export const StyledFormItem = styled(Form.Item)`
  margin-bottom: 16px;
  .ant-form-item-explain-error {
    font-size: 12px;
  }
`;

export const StyledSectionTitle = styled.h2`
  font-size: 14px;
  font-weight: 600;
  margin: 24px 0 16px 0;
`;

interface CustomSelectProps extends SelectProps {
  isMobile?: boolean;
}

export const CustomDropdown = (props: CustomSelectProps): JSX.Element => (
  <StyledDropdown
    showArrow
    isMobile={props.isMobile}
    showSearch={false}
    popupClassName='onboarding-dropdown'
    {...props}
  />
);

const StyledDropdown = styled(Select)<{ isMobile?: boolean }>`
  border-radius: 4px;

  &.ant-select:not(.ant-select-disabled):hover .ant-select-selector {
    border-color: ${({ theme }) => theme.palette.primary[0]} !important;
  }

  .ant-select-selector {
    font-size: ${(props) =>
      props.isMobile
        ? props.theme.palette.textFontSize[4]
        : props.theme.palette.textFontSize[7]};
    background-color: ${({ theme }) => theme.palette.greyscale[0]} !important;
    height: ${(props) => (props.isMobile ? '42px' : '35px')} !important;
    align-items: center;
  }
  .ant-select-selection-item {
    display: flex;
    align-items: center;
    overflow: auto;

    height: ${(props) => (props.isMobile ? '32px' : 'unset')};
    padding: 0 ${(props) => (props.isMobile ? '8px' : '6px')};
  }
  &.ant-select-disabled .ant-select-selector {
    color: #202020 !important;
  }
`;

export const InputGroup = styled.div`
  display: flex;
  gap: 16px;

  & > * {
    flex: 1;
  }
`;

export const StyledCheckbox = styled(Checkbox)`
  font-size: 12px !important;
  margin-bottom: 24px;
`;

export const ErrorMessage = styled.div`
  color: ${({ theme }) => theme.palette.error[0]};
`;

export const HelpText = styled.div`
  font-weight: 400;
  font-size: 12px;
  margin-top: -8px;
  margin-bottom: 10px;
`;

type CenteredLoaderProps = {
  className?: string;
};
export const CenteredLoader = ({
  className,
}: CenteredLoaderProps): JSX.Element => (
  <div
    className={className}
    style={{ display: 'flex', justifyContent: 'center' }}
  >
    <LoadingOutlined></LoadingOutlined>
  </div>
);

const labelStyle = {
  fontWeight: 600,
  fontSize: 12,
  marginBottom: 8,
};

const inputLabelStyle = (props) => ({
  ...labelStyle,
  ...props.labelStyle,
});

export const CustomLabel: typeof Label = (props) => (
  <Label {...props} style={{ ...labelStyle, ...props.style }} />
);
export const CustomLabelMobile: typeof Label = (props) => (
  <Label {...props} style={{ ...labelStyle, ...props.style, fontSize: 16 }} />
);
export const CustomInput: typeof Input = (props) => (
  <Input {...props} labelStyle={inputLabelStyle(props)} autoComplete='off' />
);
export const CustomInputMobile: typeof Input = (props) => (
  <Input
    {...props}
    style={{ fontSize: 16, height: 45 }}
    labelStyle={inputLabelStyle(props)}
    autoComplete='off'
  />
);

export const CustomPhoneInput: typeof PhoneInput = (props) => (
  <PhoneInput {...props} labelStyle={inputLabelStyle(props)} />
);
